// Statische Metadaten pro Canvas-Objekt-Typ. Source of Truth fuer alle Stellen die
// frueher per Switch-Case unterschieden haben (RESIZEABLE_TYPES, EDITABLE_TYPES,
// isContainer, isSemantic, Default-Bounds, Text-Field-Mapping).
//
// Compiler-Forcing-Function: `satisfies Record<ObjectType, ObjectMeta>` erzwingt einen
// Eintrag pro Union-Variante. Ein neuer Typ im Zod-Schema ohne META-Eintrag kompiliert nicht.

import type { ObjectType } from "./object.js";

export type ObjectCategory = "visual" | "semantic" | "structural";
export type ObjectTextField = "text" | "label" | "title";

export interface ObjectMeta {
  /** Grobklasse fuer UI-Gruppierung und Export-Behandlung. */
  readonly category: ObjectCategory;
  /** Struktureller Hintergrund-Container (Block/Region/Frame). Im PDF-Export hinter freie Objekte gerendert. */
  readonly isContainer: boolean;
  /** Semantisches Beratungs-Objekt (Requirement/Pain/Dependency). Region-accepts-Filter. */
  readonly isSemantic: boolean;
  /** Resize-Handles werden eingeblendet. */
  readonly isResizeable: boolean;
  /** Inline-Edit per Doppelklick/Enter. Setzt textField != null voraus. */
  readonly isEditable: boolean;
  /** Inspector-Convert-Section listet diesen Typ. */
  readonly isConvertible: boolean;
  /** Welches Feld liefert/empfaengt User-Text? null = kein editierbarer Text. */
  readonly textField: ObjectTextField | null;
  /** Soll der Inspector-Text als Multiline-Textarea gerendert werden? */
  readonly textMultiline: boolean;
  /** Default-Bounds wenn obj.width/height fehlen (Optional-Size-Typen) oder fuer Place/Convert-Defaults. */
  readonly defaultSize: { readonly width: number; readonly height: number };
  /** true = Schema verlangt explizite width/height (shape-rect/block/region/frame); false = optional. */
  readonly hasExplicitSize: boolean;
}

export const OBJECT_META = {
  sticky: {
    category: "visual",
    isContainer: false,
    isSemantic: false,
    isResizeable: true,
    isEditable: true,
    isConvertible: true,
    textField: "text",
    textMultiline: true,
    defaultSize: { width: 96, height: 96 },
    hasExplicitSize: false,
  },
  text: {
    category: "visual",
    isContainer: false,
    isSemantic: false,
    isResizeable: true,
    isEditable: true,
    isConvertible: true,
    textField: "text",
    textMultiline: true,
    defaultSize: { width: 80, height: 24 },
    hasExplicitSize: false,
  },
  "shape-rect": {
    category: "visual",
    isContainer: false,
    isSemantic: false,
    isResizeable: true,
    isEditable: true,
    isConvertible: true,
    textField: "label",
    textMultiline: false,
    defaultSize: { width: 160, height: 80 },
    hasExplicitSize: true,
  },
  requirement: {
    category: "semantic",
    isContainer: false,
    isSemantic: true,
    isResizeable: true,
    isEditable: true,
    isConvertible: true,
    textField: "text",
    textMultiline: true,
    defaultSize: { width: 200, height: 60 },
    hasExplicitSize: false,
  },
  pain: {
    category: "semantic",
    isContainer: false,
    isSemantic: true,
    isResizeable: true,
    isEditable: true,
    isConvertible: true,
    textField: "text",
    textMultiline: true,
    defaultSize: { width: 200, height: 60 },
    hasExplicitSize: false,
  },
  dependency: {
    category: "semantic",
    isContainer: false,
    isSemantic: true,
    isResizeable: true,
    isEditable: true,
    isConvertible: true,
    textField: "text",
    textMultiline: true,
    defaultSize: { width: 200, height: 60 },
    hasExplicitSize: false,
  },
  block: {
    category: "structural",
    isContainer: true,
    isSemantic: false,
    isResizeable: true,
    isEditable: true,
    isConvertible: false,
    textField: "label",
    textMultiline: false,
    defaultSize: { width: 600, height: 400 },
    hasExplicitSize: true,
  },
  region: {
    category: "structural",
    isContainer: true,
    isSemantic: false,
    isResizeable: true,
    isEditable: true,
    isConvertible: false,
    textField: "label",
    textMultiline: false,
    defaultSize: { width: 280, height: 180 },
    hasExplicitSize: true,
  },
  frame: {
    category: "structural",
    isContainer: true,
    isSemantic: false,
    isResizeable: true,
    isEditable: true,
    isConvertible: false,
    textField: "title",
    textMultiline: false,
    defaultSize: { width: 400, height: 300 },
    hasExplicitSize: true,
  },
  "mindmap-node": {
    category: "visual",
    isContainer: false,
    isSemantic: false,
    isResizeable: true,
    isEditable: true,
    isConvertible: false,
    textField: "text",
    textMultiline: false,
    defaultSize: { width: 140, height: 36 },
    hasExplicitSize: false,
  },
  shape: {
    category: "visual",
    isContainer: false,
    isSemantic: false,
    isResizeable: true,
    isEditable: true,
    isConvertible: false,
    textField: "text",
    textMultiline: false,
    defaultSize: { width: 120, height: 60 },
    hasExplicitSize: true,
  },
  icon: {
    category: "visual",
    isContainer: false,
    isSemantic: false,
    isResizeable: true,
    isEditable: false,
    isConvertible: false,
    textField: null,
    textMultiline: false,
    defaultSize: { width: 48, height: 48 },
    hasExplicitSize: true,
  },
  image: {
    category: "visual",
    isContainer: false,
    isSemantic: false,
    isResizeable: true,
    isEditable: false,
    isConvertible: false,
    textField: null,
    textMultiline: false,
    defaultSize: { width: 240, height: 160 },
    hasExplicitSize: true,
  },
} as const satisfies Record<ObjectType, ObjectMeta>;

/** Liste aller registrierten Objekt-Typen — aus dem META-Record abgeleitet. */
export const OBJECT_TYPES = Object.keys(OBJECT_META) as readonly ObjectType[];

/** Typen die in Inspector/Tool als konvertierbares Ziel angeboten werden. */
export const CONVERTIBLE_OBJECT_TYPES = (Object.keys(OBJECT_META) as ObjectType[]).filter(
  (t) => OBJECT_META[t].isConvertible,
);
